/**
 * Turn "I want a black slamfärg facade" into litres, money, hours and a
 * build-guide step.
 *
 * The litre figure is derived from the facade area the engine actually built
 * — not from a package count — so a 4 m sauna buys more paint than a 2 m one,
 * and the number moves when a wall does.
 */
import type { MaterialRow } from "../../engines/types";
import { EXTERIOR_TREATMENT_CATALOG } from "./finishCatalog";
import { DEFAULT_FINISHES, type FinishConfig } from "./types";

/**
 * Wall area → painted area. Knutbrädor, foder, sockel and vindskivor are
 * painted with the facade but are not in the wall rectangle; on a building
 * this small they add roughly a tenth of the wall area. Same direction as
 * every other margin in this BOM: over-buy slightly rather than send someone
 * back to the shop with one wall left.
 */
export const FACADE_TRIM_AREA_FACTOR = 1.1;

/**
 * The BOM identity of the facade-treatment row.
 *
 * Everything downstream — the audit classifiers, the build guide, the price
 * deltas — keys off this instead of matching the Swedish row name, so a new
 * colour needs only a catalog row.
 */
export const EXTERIOR_TREATMENT_MATERIAL_KEY = "exterior.treatment";

/** Square metres one person coats per hour, brush-applied. */
const SQM_PER_HOUR = 12;

export interface ExteriorTreatmentResult {
	/** Painted area including the trim uplift, m². */
	treatedAreaSqm: number;
	/** Whole litres to buy, all coats included. 0 when untreated. */
	litres: number;
	coats: number;
	/** Brush hours, all coats. 0 when untreated. */
	applicationHours: number;
	/** Hours the facade must stand before handover. 0 when untreated. */
	dryingHours: number;
	/** Empty when untreated — an untreated facade buys nothing. */
	materials: MaterialRow[];
}

export function calculateExteriorTreatment(input: {
	/** Wall area the engine built, m². */
	facadeAreaSqm: number;
	finishes?: FinishConfig;
}): ExteriorTreatmentResult {
	const finishes = input.finishes ?? DEFAULT_FINISHES;
	const spec = EXTERIOR_TREATMENT_CATALOG[finishes.exteriorTreatment];

	if (spec.coats === 0 || !spec.priceRuleId) {
		return {
			treatedAreaSqm: 0,
			litres: 0,
			coats: 0,
			applicationHours: 0,
			dryingHours: 0,
			materials: [],
		};
	}

	const treatedAreaSqm =
		Math.max(0, input.facadeAreaSqm) * FACADE_TRIM_AREA_FACTOR;
	const litres = Math.ceil(
		(treatedAreaSqm * spec.coats) / spec.coverageSqmPerLitre,
	);
	const applicationHours =
		Math.round(((treatedAreaSqm * spec.coats) / SQM_PER_HOUR) * 2) / 2;

	return {
		treatedAreaSqm: Math.round(treatedAreaSqm * 10) / 10,
		litres,
		coats: spec.coats,
		applicationHours,
		dryingHours: spec.dryingHours,
		materials: [
			{
				material: spec.bomNameSv,
				// The dimension cell states the assumption the litre count rests
				// on, so an over/under-buy is traceable without opening the code.
				dimension: `${spec.coats} strykning${spec.coats > 1 ? "ar" : ""}, ${spec.coverageSqmPerLitre} m²/liter`,
				quantity: `${litres} liter`,
				phase: "roofExterior",
				priceRuleId: spec.priceRuleId,
				materialKey: EXTERIOR_TREATMENT_MATERIAL_KEY,
			},
		],
	};
}
